import React, { useEffect, useState, useCallback } from 'react';
import { brainAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Mail, Send, Eye, BarChart3, RefreshCw, Loader2,
  TrendingUp, Clock, Zap, Target, ArrowUpRight, Brain,
  Lightbulb, AlertTriangle, Rocket, Bell, ChevronDown, ChevronUp,
  Sparkles, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Area, AreaChart
} from 'recharts';

const CHART_COLORS = ['hsl(204,94%,40%)', 'hsl(171,66%,35%)', 'hsl(27,90%,52%)', 'hsl(220,12%,45%)', 'hsl(4,82%,55%)'];

const STATUS_LABELS = { draft: 'Draft', approved: 'Approved', scheduled: 'Scheduled', sent: 'Sent', dismissed: 'Dismissed' };
const SOURCE_LABELS = { auto_gen: 'Auto-Gen', ai_cadence: 'Cadence', brain: 'Brain', onboarding_brain: 'Onboarding' };
const METHOD_LABELS = { smtp: 'SMTP', sendgrid: 'SendGrid', demo: 'Demo', unknown: 'Unknown' };

export default function EmailEngagementDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [autoConfig, setAutoConfig] = useState(null);
  const [showCharts, setShowCharts] = useState(false);
  const [pushingAlert, setPushingAlert] = useState(false);
  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brainAPI.getEmailEngagementAnalytics();
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, []);

  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const res = await brainAPI.getEmailEngagementInsights();
      setInsights(res.data);
    } catch { toast.error('Failed to generate insights'); }
    finally { setInsightsLoading(false); }
  }, []);

  const fetchAutoConfig = useCallback(async () => {
    try {
      const res = await brainAPI.getAutoInsightsConfig();
      setAutoConfig(res.data);
    } catch { /* silent */ }
  }, []);

  const toggleAuto = useCallback(async (enabled) => {
    try {
      await brainAPI.toggleAutoInsights(enabled);
      setAutoConfig(c => ({ ...c, auto_enabled: enabled }));
      toast.success(enabled ? 'Weekly auto-insights enabled' : 'Auto-insights paused');
    } catch { toast.error('Failed to update'); }
  }, []);

  const pushToAlerts = useCallback(async () => {
    setPushingAlert(true);
    try {
      const res = await brainAPI.triggerAutoInsights();
      if (res.data.status === 'ok') {
        toast.success('Insight pushed to Brain Alerts');
        fetchAutoConfig();
      } else {
        toast.info(res.data.reason || 'No insight generated');
      }
    } catch { toast.error('Failed to push insight'); }
    finally { setPushingAlert(false); }
  }, [fetchAutoConfig]);

  useEffect(() => { fetchAnalytics(); fetchAutoConfig(); }, [fetchAnalytics, fetchAutoConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="engagement-loading">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-[hsl(204,94%,40%)] mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading engagement data...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { overview, status_breakdown, method_breakdown, source_breakdown, top_subjects, volume_trend, hour_heatmap } = data;
  const statusData = status_breakdown.map(s => ({ ...s, name: STATUS_LABELS[s.name] || s.name }));
  const sourceData = source_breakdown.map(s => ({ ...s, name: SOURCE_LABELS[s.name] || s.name }));
  const methodData = method_breakdown.map(s => ({ ...s, name: METHOD_LABELS[s.name] || s.name }));

  return (
    <div className="space-y-8 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="email-engagement-dashboard">

      {/* ── Hero Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 rounded-xl bg-[hsl(204,94%,40%)]/10">
              <Activity className="w-5 h-5 text-[hsl(204,94%,40%)]" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Email Engagement
            </h1>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            Your outreach performance at a glance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/brain/email-outbox')} data-testid="go-to-outbox-btn" className="text-muted-foreground hover:text-foreground">
            <Mail className="w-4 h-4 mr-1.5" /> Outbox
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="refresh-analytics-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Empty-state — no emails sent (Iter 59c) */}
      {overview.total_sent === 0 && overview.total_emails === 0 && (
        <Card data-testid="engagement-empty">
          <CardContent className="py-10 text-center">
            <Mail className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium">No email engagement to analyze yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              Open/click/reply metrics populate automatically once your cadence sends its first email. Pixel-tracking + SendGrid Event Webhook are already live — you just need your first send.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/outreach/email-outbox'} data-testid="engagement-go-outbox-btn">
                Open Email Outbox
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.location.href = '/leads'} data-testid="engagement-go-pipeline-btn">
                Go to Pipeline
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── KPI Strip ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        <KPICard icon={Mail} label="Total Emails" value={overview.total_emails} testId="kpi-total-emails" />
        <KPICard icon={Send} label="Sent" value={overview.total_sent} testId="kpi-total-sent" variant="primary" />
        <KPICard icon={Eye} label="Opened" value={overview.total_opened} sub={`${overview.total_opens} total opens`} testId="kpi-total-opened" variant="accent" />
        <KPICard icon={TrendingUp} label="Open Rate" value={`${overview.open_rate}%`} testId="kpi-open-rate" variant="highlight" />
      </div>

      {/* ── AI Brain Insights (Hero Section) ── */}
      <AIInsightsPanel
        insights={insights}
        loading={insightsLoading}
        onGenerate={fetchInsights}
        autoConfig={autoConfig}
        onToggleAuto={toggleAuto}
        onPushAlert={pushToAlerts}
        pushingAlert={pushingAlert}
      />

      {/* ── Volume Trend ── */}
      <Card className="border-0 shadow-sm" data-testid="volume-trend-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <BarChart3 className="w-4 h-4" /> Email Volume Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          {volume_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={volume_trend}>
                <defs>
                  <linearGradient id="gSent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(204,94%,40%)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="hsl(204,94%,40%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOpened" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(171,66%,35%)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="hsl(171,66%,35%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }} />
                <Area type="monotone" dataKey="sent" name="Sent" stroke="hsl(204,94%,40%)" fill="url(#gSent)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="opened" name="Opened" stroke="hsl(171,66%,35%)" fill="url(#gOpened)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart msg="No email activity yet" />}
        </CardContent>
      </Card>

      {/* ── Expandable Deep Dive ── */}
      <div>
        <button
          onClick={() => setShowCharts(!showCharts)}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-4"
          data-testid="toggle-deep-dive"
        >
          {showCharts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showCharts ? 'Hide' : 'Show'} Deep Dive Analytics
        </button>

        {showCharts && (
          <div className="space-y-5 animate-in fade-in slide-in-from-top-2 duration-300">
            {/* Hour heatmap + Pie breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border-0 shadow-sm" data-testid="hour-heatmap-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" /> Opens by Hour (UTC)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hour_heatmap.some(h => h.opens > 0) ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={hour_heatmap}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#9ca3af' }} tickFormatter={h => `${h}h`} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <RTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }} formatter={v => [v, 'Opens']} labelFormatter={l => `${l}:00 UTC`} />
                        <Bar dataKey="opens" fill="hsl(171,66%,35%)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyChart msg="No open data yet" />}
                </CardContent>
              </Card>

              <MiniPie title="Status" data={statusData} testId="status-pie-chart" />
              <MiniPie title="Source" data={sourceData} testId="source-pie-chart" />
            </div>

            {/* Method breakdown + Top subjects */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <MiniPie title="Send Method" data={methodData} testId="method-pie-chart" />

              <Card className="lg:col-span-2 border-0 shadow-sm" data-testid="top-subjects-table">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                    <TrendingUp className="w-4 h-4" /> Top Performing Subjects
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {top_subjects.length > 0 ? (
                    <div className="space-y-2">
                      {top_subjects.slice(0, 5).map((s, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/40 transition-colors" data-testid={`subject-row-${i}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs text-muted-foreground w-5 shrink-0">{i + 1}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate max-w-[320px]">{s.subject}</p>
                              {s.lead_name && (
                                <p className="text-xs text-muted-foreground">
                                  {s.lead_name}
                                  {s.lead_id && (
                                    <button onClick={() => navigate(`/leads/${s.lead_id}`)} className="ml-1 text-[hsl(204,94%,40%)] hover:underline">
                                      <ArrowUpRight className="w-3 h-3 inline" />
                                    </button>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge variant="secondary" className="bg-muted/60 text-muted-foreground border-0 shrink-0">
                            <Eye className="w-3 h-3 mr-1" />{s.opens}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyChart msg="Send some emails to see performance" />}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Sub-Components ── */

function KPICard({ icon: Icon, label, value, sub, testId, variant }) {
  const styles = {
    primary: { icon: 'bg-[hsl(204,94%,40%)]/10 text-[hsl(204,94%,40%)]', ring: '' },
    accent: { icon: 'bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)]', ring: '' },
    highlight: { icon: 'bg-emerald-50 text-emerald-600', ring: 'ring-1 ring-emerald-200/50' },
    default: { icon: 'bg-muted text-muted-foreground', ring: '' },
  };
  const s = styles[variant] || styles.default;

  return (
    <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${s.ring}`} data-testid={testId}>
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className={`p-1.5 rounded-lg ${s.icon.split(' ')[0]}`}>
            <Icon className={`w-3.5 h-3.5 ${s.icon.split(' ')[1]}`} />
          </div>
          <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest">{label}</span>
        </div>
        <p className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function MiniPie({ title, data, testId }) {
  if (!data.length) return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-5"><EmptyChart msg={`No ${title.toLowerCase()} data`} /></CardContent>
    </Card>
  );
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55} innerRadius={32} paddingAngle={2} strokeWidth={0}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <RTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
              {d.name} ({d.value})
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyChart({ msg }) {
  return (
    <div className="flex flex-col items-center justify-center h-32 text-muted-foreground/50">
      <BarChart3 className="w-6 h-6 mb-1.5" />
      <p className="text-xs">{msg}</p>
    </div>
  );
}

/* ── AI Insights Panel ── */

const INSIGHT_ICONS = {
  timing: Clock,
  content: Lightbulb,
  strategy: Rocket,
  warning: AlertTriangle,
  opportunity: TrendingUp,
};

const INSIGHT_COLORS = {
  timing: { bg: 'bg-sky-50', border: 'border-sky-200/60', text: 'text-sky-700', icon: 'text-sky-500' },
  content: { bg: 'bg-amber-50', border: 'border-amber-200/60', text: 'text-amber-800', icon: 'text-amber-500' },
  strategy: { bg: 'bg-teal-50', border: 'border-teal-200/60', text: 'text-teal-800', icon: 'text-teal-500' },
  warning: { bg: 'bg-rose-50', border: 'border-rose-200/60', text: 'text-rose-800', icon: 'text-rose-500' },
  opportunity: { bg: 'bg-emerald-50', border: 'border-emerald-200/60', text: 'text-emerald-800', icon: 'text-emerald-500' },
};

const PRI_DOT = { high: 'bg-rose-400', medium: 'bg-amber-400', low: 'bg-slate-300' };

function AIInsightsPanel({ insights, loading, onGenerate, autoConfig, onToggleAuto, onPushAlert, pushingAlert }) {
  const hasInsights = insights?.insights?.length > 0;
  const lastRun = autoConfig?.last_auto_run;
  const autoEnabled = autoConfig?.auto_enabled !== false;

  return (
    <Card className="border-0 shadow-sm overflow-hidden relative" data-testid="ai-insights-panel">
      {/* Subtle gradient accent line */}
      <div className="h-0.5 bg-gradient-to-r from-[hsl(204,94%,40%)] via-[hsl(171,66%,35%)] to-[hsl(27,90%,52%)]" />

      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-gradient-to-br from-[hsl(204,94%,40%)]/15 to-[hsl(171,66%,35%)]/10">
              <Brain className="w-5 h-5 text-[hsl(204,94%,40%)]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  AI Brain Insights
                </CardTitle>
                {hasInsights && (
                  <Badge variant="secondary" className="bg-[hsl(204,94%,40%)]/10 text-[hsl(204,94%,40%)] border-0 text-[10px] font-medium">
                    {insights.insights.length} insights
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {lastRun ? `Last analysis: ${new Date(lastRun).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'Analyze your email patterns with AI'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Auto-insights toggle */}
            <div className="flex items-center gap-2" data-testid="auto-insights-toggle">
              <span className="text-[11px] text-muted-foreground">Weekly auto</span>
              <Switch
                checked={autoEnabled}
                onCheckedChange={onToggleAuto}
                className="scale-75"
              />
            </div>

            {/* Push to alerts */}
            {hasInsights && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onPushAlert}
                disabled={pushingAlert}
                data-testid="push-to-alerts-btn"
                className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
              >
                {pushingAlert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Push to Alerts
              </Button>
            )}

            {/* Generate / Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerate}
              disabled={loading}
              data-testid="generate-insights-btn"
              className="gap-1.5"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {hasInsights ? 'Refresh' : 'Analyze'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-2 pb-5">
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-3" data-testid="insights-loading">
            <div className="relative">
              <div className="w-10 h-10 border-2 border-[hsl(204,94%,40%)]/20 border-t-[hsl(204,94%,40%)] rounded-full animate-spin" />
              <Brain className="w-4 h-4 text-[hsl(204,94%,40%)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div>
              <p className="text-sm font-medium">Analyzing your email patterns...</p>
              <p className="text-[11px] text-muted-foreground">This takes a few seconds</p>
            </div>
          </div>
        ) : hasInsights ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {insights.insights.map((insight, i) => {
              const colors = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.strategy;
              const Icon = INSIGHT_ICONS[insight.type] || Sparkles;
              return (
                <div
                  key={i}
                  className={`group p-4 rounded-xl border ${colors.border} ${colors.bg} transition-all hover:shadow-md hover:-translate-y-0.5 duration-200`}
                  data-testid={`insight-card-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${colors.bg} mt-0.5 shrink-0`}>
                      <Icon className={`w-4 h-4 ${colors.icon}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h4 className={`text-sm font-semibold leading-tight ${colors.text}`}>{insight.title}</h4>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRI_DOT[insight.priority] || PRI_DOT.medium}`} title={`${insight.priority} priority`} />
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{insight.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="insights-empty">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-[hsl(204,94%,40%)]/5 to-[hsl(171,66%,35%)]/5 mb-4">
              <Brain className="w-10 h-10 text-muted-foreground/30" />
            </div>
            <p className="text-sm font-medium mb-1">Get AI-powered recommendations</p>
            <p className="text-xs text-muted-foreground mb-4 max-w-xs">The Brain will analyze your email patterns and suggest ways to improve open rates, timing, and content.</p>
            <Button onClick={onGenerate} size="sm" className="gap-2 bg-[hsl(204,94%,40%)] hover:bg-[hsl(204,94%,45%)]" data-testid="generate-insights-cta">
              <Sparkles className="w-4 h-4" /> Generate Insights
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
